/**
 * Calculate returnable packaging lines from credited bundle products
 *
 * @param {Array} creditProducts - Array of products being credited (with negative itemCount)
 * @param {Array} allProducts - All products from useProducts
 * @param {Function} getProductById - Function to get product by ID from useProducts
 * @returns {Object} - { packagingLines: Array, missingPackaging: Array }
 */
export const calculateBundlePackaging = (creditProducts, allProducts, getProductById) => {
  // Map to aggregate packaging by productId
  const packagingMap = new Map();
  const missingPackaging = [];

  creditProducts.forEach((creditProduct) => {
    // Only process bundle products that are being credited (negative itemCount)
    // Skip additions (positive itemCount) as they don't require packaging returns
    if (creditProduct.itemCount >= 0) {
      return; // Skip additions
    }

    const fullProduct = getProductById(creditProduct.productId);

    if (!fullProduct || !fullProduct.isBundle) {
      return; // Skip non-bundle products
    }

    // Check if bundle has packaging material
    if (!fullProduct.packagingMaterial || !fullProduct.packagingMaterial.productId) {
      return; // Skip bundles without packaging
    }

    const packagingProductId = fullProduct.packagingMaterial.productId;
    const packagingProduct = getProductById(packagingProductId);

    // Validate packaging product exists and is returnable
    if (!packagingProduct) {
      missingPackaging.push({
        bundleId: creditProduct.productId,
        bundleName: creditProduct.productName,
        packagingId: packagingProductId,
        reason: "not_found",
      });
      return;
    }

    if (packagingProduct.productGroup !== "Returnable packaging") {
      // Not returnable packaging - skip silently
      return;
    }

    // Calculate quantity: number of bundles being credited (absolute value)
    const bundleQuantity = Math.abs(creditProduct.itemCount);
    const packagingQuantityPerBundle = fullProduct.packagingMaterial.quantity || 1;
    const totalPackagingQuantity = bundleQuantity * packagingQuantityPerBundle;

    // Aggregate packaging in map
    if (packagingMap.has(packagingProductId)) {
      const existing = packagingMap.get(packagingProductId);
      existing.itemCount += totalPackagingQuantity;
    } else {
      packagingMap.set(packagingProductId, {
        productId: packagingProductId,
        productName: packagingProduct.productName,
        productPrice: (packagingProduct.price ?? 0).toString(), // Cart-line field; source is the product's own price
        itemCount: totalPackagingQuantity, // Positive quantity
        isPackagingLine: true, // Flag to identify packaging lines
        externalId: packagingProduct.externalId || packagingProduct.exactId,
        productBarcode: packagingProduct.barcode || "",
        productImageDownloadURL: packagingProduct.productImageDownloadURL || "",
        productCategory: packagingProduct.productCategory || "",
        productVat: packagingProduct.vat?.nl || null,
        orderable: packagingProduct.orderable !== false,
      });
    }
  });

  const packagingLines = Array.from(packagingMap.values());

  return {
    packagingLines,
    missingPackaging,
  };
};
